import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { ColDef } from "@/components/admin/shared/AdminTable";
import { AdminTable } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import { PageHeader, AddButton, SaveButton, CancelButton, ViewButton, EditButton, DeleteButton } from "@/components/admin/shared/PageHeader";
import { PennField, PennInput, PennTextarea } from "@/components/ui";

const schema = z.object({
  id: z.string().min(1, "ID is required"),
  label: z.string().min(1, "Label is required"),
  dataJson: z.string().refine((v) => {
    if (!v.trim()) return true;
    try { JSON.parse(v); return true; } catch { return false; }
  }, "Must be valid JSON"),
});
type FormData = z.infer<typeof schema>;
type Item = FormData & { _id: number };
type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: Item } | null;

export const JsonDatasetPage = ({ title, subtitle }: { title: string; subtitle?: string }) => {
  const [items, setItems] = useState<Item[]>([]);
  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<Item | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormData>({ resolver: zodResolver(schema) });
  const blank: FormData = { id: "", label: "", dataJson: "" };

  const openAdd = () => { reset(blank); setModal({ mode: "add" }); };
  const openEdit = (item: Item) => { reset(item); setModal({ mode: "edit", item }); };
  const openView = (item: Item) => { reset(item); setModal({ mode: "view", item }); };
  const onSubmit = (data: FormData) => {
    if (modal?.mode === "edit") setItems((p) => p.map((i) => i._id === modal.item!._id ? { ...i, ...data } : i));
    else setItems((p) => [...p, { _id: Date.now(), ...data }]);
    setModal(null);
  };

  const isView = modal?.mode === "view";

  const cols: ColDef<Item>[] = [
    { header: "ID", key: "id" },
    { header: "Label", key: "label" },
    { header: "Data (JSON)", render: (i) => <span className="font-mono text-blue_grey4 truncate max-w-xs block text-small">{i.dataJson}</span> },
    { header: "Actions", render: (i) => <div className="flex space-x-2"><ViewButton onClick={() => openView(i)} /><EditButton onClick={() => openEdit(i)} /><DeleteButton onClick={() => setDeleteTarget(i)} /></div> },
  ];

  const titleMap = { add: `Add ${title} record`, edit: `Edit ${title} record`, view: `View ${title} record` };

  return (
    <>
      <PageHeader title={`Dataset: ${title}`} subtitle={subtitle} action={<AddButton onClick={openAdd} label={`Add ${title} record`} />} />
      <AdminTable columns={cols} rows={items} keyExtractor={(i) => i._id} emptyMessage={`No ${title} records yet.`} />

      <AdminModal open={modal !== null} onClose={() => setModal(null)} title={modal ? titleMap[modal.mode] : ""} maxWidth="max-w-lg"
        footer={
          isView ? <CancelButton onClick={() => setModal(null)} /> :
          <><CancelButton onClick={() => setModal(null)} /><SaveButton onClick={handleSubmit(onSubmit)} /></>
        }>
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PennField label="ID" htmlFor="id" required={!isView} error={!isView ? errors.id?.message : undefined}>
              <PennInput id="id" disabled={isView} error={!isView && !!errors.id} {...register("id")} />
            </PennField>
            <PennField label="Label" htmlFor="label" required={!isView} error={!isView ? errors.label?.message : undefined}>
              <PennInput id="label" disabled={isView} error={!isView && !!errors.label} {...register("label")} />
            </PennField>
          </div>
          <PennField label="Data (JSON)" htmlFor="dataJson" error={!isView ? errors.dataJson?.message : undefined}>
            <PennTextarea id="dataJson" rows={5} disabled={isView} error={!isView && !!errors.dataJson} {...register("dataJson")} />
          </PennField>
        </form>
      </AdminModal>

      <DeleteConfirmModal open={deleteTarget !== null} onClose={() => setDeleteTarget(null)} onConfirm={() => setItems((p) => p.filter((i) => i._id !== deleteTarget!._id))} itemLabel={deleteTarget?.label} />
    </>
  );
};
